import { orderBy } from 'lodash-es'
import { action } from 'mobx'
import type { PrimeMemberFn } from '@/features/iam'
import { LegendVersionId } from '@/features/jobs/legends/PublishedLegend'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import { maybeParseDate } from '@/utils/date-util'
import { memoize } from '@/utils/memoize-util'
import {
  type LegendBuilderApi,
  SUPPORTED_ENGINE_VERSION,
} from '@/features/legends/api/legends-api'
import type {
  ComputeFailedResource,
  LegendConflictResource,
  LegendContentResource,
  LegendResource,
  SaveLegendVersionRequest,
} from '@/features/legends/api/resources/legend'
import type { LegendDocument } from '@/features/legends/editor/legend-document'
import { mapServiceTypeResourceToServiceType } from '@/features/legends/options/legend-options-mappers'
import {
  mapLegendComputationInputToRequest,
  mapComputeLegendVersionResultResourceToComputation,
} from '@/features/legends/preview/legend-computation-mappers'
import type {
  LegendComputation,
  LegendComputationInput,
} from '@/features/legends/preview/legend-computation'
import { Legend } from './Legend'
import { diffLegendContentResources } from './legend-content-diff'
import { LegendComputeError } from './legend-compute-error'
import { LegendConflictError } from './legend-conflict-error'
import {
  mapLegendContentResourceToDocument,
  mapLegendDocumentToContentResource,
} from './legend-document-mappers'
import type {
  EngineSymbols,
  LegendAnalyzerError,
  OrganizerBindings,
} from './legend-engine'
import {
  mapLegendActivationResourceToProps,
  mapLegendErrorResourceToAnalyzerError,
  mapLegendResourceToProps,
  mapOrganizerBindingCatalogsResourceToBindings,
  mapSaveLegendVersionResultResourceToProps,
} from './legend-mappers'
import { LegendVersion, type LegendVersionProps } from './LegendVersion'

/**
 * Query for listing a workspace's legends.
 */
export interface ListLegendsQuery extends Partial<CursorPageQuery> {
  workspaceId: Id
}

/**
 * Query for fetching a legend version.
 */
export interface FetchLegendVersionQuery {
  legendId: Id
  /**
   * The version number; the latest version when not given.
   */
  version?: number
}

/**
 * Params for creating a legend.
 */
export interface CreateLegendParams {
  name: string
  workspaceId: Id
}

/**
 * Params for saving a legend version.
 */
export interface SaveLegendVersionParams {
  legendId: Id
  version: number
  workspaceId: Id
  /**
   * From the version's fetch or its last save: when someone else saved since,
   * the save fails with a conflict.
   */
  concurrencyVersion: number
  document: LegendDocument
  /**
   * Saved with the version, for the publish that follows.
   */
  versionNotes?: string
  /**
   * Saves over changes someone else made since the version was loaded.
   */
  force?: boolean
}

/**
 * Params for saving the changes to a legend version.
 */
export interface SaveLegendVersionChangesParams extends SaveLegendVersionParams {
  /**
   * The document as the version has it; only the parts that differ from it
   * are sent.
   */
  previous: LegendDocument
}

/**
 * Params for publishing a legend version.
 */
export interface PublishLegendVersionParams {
  legendId: Id
  version: number
  concurrencyVersion: number
  versionNotes: string
}

/**
 * Params for running a legend version through the engine.
 */
export interface ComputeLegendVersionParams {
  legendId: Id
  version: number
  input: LegendComputationInput
}

/**
 * The outcome of saving a legend version.
 */
export interface SavedLegendVersion {
  /**
   * The version's concurrency version after the save, which the next save
   * sends.
   */
  concurrencyVersion: number
  errors: LegendAnalyzerError[]
}

/**
 * Loads and caches legends and their versions, and saves drafts.
 */
export class LegendStore {
  readonly legends = collection<Legend>({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new Legend(),
  })

  /**
   * The legend versions, one instance per legend and version number.
   */
  readonly legendVersions = collection<LegendVersion, string>({
    getDataId: (d) => getVersionKey(d.id, d.version ?? -1),
    getModelId: (m) => LegendVersionId.asString(m.versionId),
    create: () => new LegendVersion(),
  })

  /**
   * Fetches the symbols the engine provides to formulas, once.
   */
  readonly fetchEngineSymbols = memoize(
    async (signal?: AbortSignal): Promise<EngineSymbols> =>
      (await this._api.engineSymbols.get(signal)).identifiers,
    { promise: true, keySelector: () => 'engineSymbols' },
  )

  /**
   * Fetches the personal organizer fields legends can prefill and collect,
   * once.
   */
  readonly fetchOrganizerBindings = memoize(
    async (signal?: AbortSignal): Promise<OrganizerBindings> =>
      mapOrganizerBindingCatalogsResourceToBindings(
        await this._api.organizerBindings.get(signal),
      ),
    { promise: true, keySelector: () => 'organizerBindings' },
  )

  /**
   * The versions saved through this store, by key, whose concurrency version
   * is from the save.
   */
  private readonly _savedVersionKeys = new Set<string>()

  constructor(
    private readonly _api: LegendBuilderApi,
    private readonly _primeMember: PrimeMemberFn,
  ) {}

  /**
   * The loaded legends of the workspace.
   *
   * @param workspaceId
   */
  legendsForWorkspace(workspaceId: Id): Legend[] {
    return this.legends.filter((legend) => legend.workspaceId === workspaceId)
  }

  /**
   * The legend's loaded versions, latest first.
   *
   * @param legendId
   */
  versionsFor(legendId: Id): LegendVersion[] {
    return orderBy(
      this.legendVersions.filter((version) => version.id === legendId),
      (version) => version.version,
      'desc',
    )
  }

  /**
   * The legend's latest loaded version.
   *
   * @param legendId
   */
  latestVersionFor(legendId: Id): LegendVersion | undefined {
    return this.versionsFor(legendId)[0]
  }

  /**
   * A loaded version of the legend.
   *
   * @param legendId
   * @param version
   */
  legendVersionFor(legendId: Id, version: number): LegendVersion | undefined {
    return this.legendVersions.get(getVersionKey(legendId, version))
  }

  /**
   * Lists a page of the workspace's legends, with their latest versions,
   * including drafts and inactive legends.
   *
   * @param query
   * @param signal
   */
  async listLegends(
    query: ListLegendsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Legend>> {
    const response = await this._api.legends.list(
      Id.assert(query.workspaceId),
      {
        limit: query.limit,
        after: query.after,
        inactive: true,
        draft: true,
      },
      signal,
    )
    return {
      data: this._setLegends(response.data),
      next: response.cursor,
      total: response.total,
    }
  }

  /**
   * Lists a page of the legend's versions, latest first.
   *
   * @param legendId
   * @param query
   * @param signal
   */
  async listLegendVersions(
    legendId: Id,
    query: Partial<CursorPageQuery>,
    signal?: AbortSignal,
  ): Promise<CursorPaged<LegendVersion>> {
    const response = await this._api.versions.list(
      Id.assert(legendId),
      { limit: query.limit, after: query.after },
      signal,
    )
    return {
      data: this._setVersions(response.data),
      next: response.cursor,
      total: response.total,
    }
  }

  /**
   * Fetches a version of the legend with its content. A version fetched or
   * saved before is reused; the latest version is always fetched.
   *
   * @param query
   * @param signal
   */
  async fetchLegendVersion(
    { legendId, version }: FetchLegendVersionQuery,
    signal?: AbortSignal,
  ): Promise<LegendVersion> {
    if (version !== undefined) {
      const cached = this.legendVersionFor(legendId, version)
      if (cached?.document) {
        return cached
      }
    }

    const resource = await this._api.versions.get(
      Id.assert(legendId),
      version ?? 'latest',
      signal,
    )
    this._setLegends([resource])
    // SAFETY: `_setLegends` sets the version.
    return this.legendVersionFor(resource.id, resource.version)!
  }

  /**
   * Creates a legend with an empty draft.
   *
   * @param params
   */
  async createLegend(params: CreateLegendParams): Promise<Legend> {
    const resource = await this._api.legends.create({
      name: params.name,
      workspace_id: Id.assert(params.workspaceId),
    })
    const [legend] = this._setLegends([resource])
    return legend
  }

  /**
   * Saves the whole version, creating it when it doesn't exist yet.
   *
   * @param params
   */
  saveLegendVersion(params: SaveLegendVersionParams) {
    return this._saveLegendVersion(
      'update',
      params,
      mapLegendDocumentToContentResource(params.document),
    )
  }

  /**
   * Saves the parts of the version that changed. Removing a part saves the
   * whole version, since a patch can't remove one.
   *
   * @param params
   */
  saveLegendVersionChanges({
    previous,
    ...params
  }: SaveLegendVersionChangesParams) {
    const content = mapLegendDocumentToContentResource(params.document)
    const changes = diffLegendContentResources(
      mapLegendDocumentToContentResource(previous),
      content,
    )
    return changes
      ? this._saveLegendVersion('patch', params, changes)
      : this._saveLegendVersion('update', params, content)
  }

  /**
   * Publishes the version, which makes it the one new jobs use.
   *
   * @param params
   */
  async publishLegendVersion(
    params: PublishLegendVersionParams,
  ): Promise<LegendVersion> {
    const resource = await this._api.versions.publish(
      Id.assert(params.legendId),
      params.version,
      {
        concurrency_version: params.concurrencyVersion,
        version_notes: params.versionNotes,
      },
    )
    this._setLegends([resource])
    // SAFETY: `_setLegends` sets the version.
    return this.legendVersionFor(resource.id, resource.version)!
  }

  /**
   * Makes the legend available for new jobs.
   *
   * @param legend
   */
  async activateLegend(legend: Legend): Promise<Legend> {
    const result = await this._api.legends.activate(Id.assert(legend.id))
    return legend.set(mapLegendActivationResourceToProps(result))
  }

  /**
   * Stops the legend from being used for new jobs.
   *
   * @param legend
   */
  async deactivateLegend(legend: Legend): Promise<Legend> {
    const result = await this._api.legends.deactivate(Id.assert(legend.id))
    return legend.set(mapLegendActivationResourceToProps(result))
  }

  /**
   * Runs the version through the engine with sample input.
   *
   * @param params
   * @param signal
   */
  async computeLegendVersion(
    params: ComputeLegendVersionParams,
    signal?: AbortSignal,
  ): Promise<LegendComputation> {
    const resource = await this._api.versions
      .compute(
        Id.assert(params.legendId),
        params.version,
        mapLegendComputationInputToRequest(params.input),
        signal,
      )
      .catch((err: unknown) => {
        throw mapComputeError(err)
      })
    return mapComputeLegendVersionResultResourceToComputation(resource)
  }

  private async _saveLegendVersion(
    method: 'update' | 'patch',
    params: SaveLegendVersionParams,
    content: Partial<LegendContentResource>,
  ): Promise<SavedLegendVersion> {
    const legendId = Id.assert(params.legendId)
    const { concurrencyVersion } = params
    const request: SaveLegendVersionRequest = {
      ...content,
      version: params.version,
      concurrency_version: concurrencyVersion,
      id: legendId,
      workspace_id: Id.assert(params.workspaceId),
      engine_version: SUPPORTED_ENGINE_VERSION,
      force: params.force,
    }
    if (params.versionNotes !== undefined) {
      request.version_notes = params.versionNotes
    }
    const result = await this._api.versions[method](
      legendId,
      params.version,
      request,
    ).catch((err: unknown) => {
      throw this._mapSaveError(err, params.workspaceId)
    })

    this._setSavedVersion({
      ...mapSaveLegendVersionResultResourceToProps(
        legendId,
        params.version,
        params.workspaceId,
        result,
      ),
      document: params.document,
    })
    return {
      concurrencyVersion: result.concurrency_version ?? concurrencyVersion,
      errors: (result.errors ?? []).map(mapLegendErrorResourceToAnalyzerError),
    }
  }

  /**
   * Also adds the draft that saving changes to the published version creates.
   */
  @action
  private _setSavedVersion(props: Partial<LegendVersionProps>) {
    const saved = this.legendVersions.set(props)
    this._savedVersionKeys.add(getVersionKey(saved.id, saved.version))
  }

  private _mapSaveError(err: unknown, workspaceId: Id) {
    // SAFETY: nothing is read before `type` is checked, which copes with any
    // value.
    const conflict = err as Partial<LegendConflictResource> | null
    if (conflict?.type !== 'LegendConflict') {
      return err
    }

    return new LegendConflictError(
      this._primeMember({
        workspaceId: Id(conflict.workspace_id) ?? workspaceId,
        userId: Id(conflict.last_updated_by_id),
        userPublicId: Id(conflict.last_updated_by_public_id),
      }),
      err,
    )
  }

  @action
  private _setLegends(resources: LegendResource[]): Legend[] {
    this._setVersions(resources)
    return this.legends.set(resources.map(mapLegendResourceToProps))
  }

  @action
  private _setVersions(resources: LegendResource[]): LegendVersion[] {
    return this.legendVersions.set(
      resources.map((resource) => {
        const props = this._mapLegendVersionResourceToProps(resource)
        // A listing's concurrency version may be for content newer than the
        // one fetched or saved before, which taking it would let a save of
        // the stale content overwrite without a conflict. Or it may be older
        // than the save's, when the listing was requested before the save
        // finished, which would fail the next save with a false conflict.
        const hasOwnConcurrencyVersion =
          !!this.legendVersionFor(resource.id, resource.version)?.document ||
          this._savedVersionKeys.has(
            getVersionKey(resource.id, resource.version),
          )
        return resource.stories || !hasOwnConcurrencyVersion
          ? props
          : { ...props, concurrencyVersion: undefined }
      }),
    )
  }

  private _mapLegendVersionResourceToProps(
    resource: LegendResource,
  ): Partial<LegendVersionProps> {
    return {
      id: resource.id,
      version: resource.version,
      workspaceId: resource.workspace_id,
      concurrencyVersion: resource.concurrency_version,
      name: resource.name ?? '',
      description: resource.description ?? '',
      serviceType: resource.service_type
        ? mapServiceTypeResourceToServiceType(resource.service_type)
        : null,
      published: resource.published ?? false,
      dateModified: maybeParseDate(resource.date_modified) ?? undefined,
      datePublished: maybeParseDate(resource.date_published),
      versionNotes: resource.version_notes ?? '',
      viewOrder: resource.view_order ?? 0,
      lastUpdatedBy: resource.last_updated_by_id
        ? this._primeMember({
            workspaceId: resource.workspace_id,
            userId: resource.last_updated_by_id,
            userPublicId: Id(resource.last_updated_by_public_id),
          })
        : undefined,
      // Version listings leave out the content, which must not wipe the
      // content of a version fetched before.
      document: resource.stories
        ? mapLegendContentResourceToDocument(resource)
        : undefined,
    }
  }
}

function getVersionKey(legendId: Id, version: number) {
  return LegendVersionId.asString(LegendVersionId(legendId ?? '', version))
}

function mapComputeError(err: unknown) {
  // SAFETY: nothing is read before `type` is checked, which copes with any
  // value.
  const failure = err as Partial<ComputeFailedResource> | null
  if (failure?.type !== 'ComputeFailed') {
    return err
  }

  return new LegendComputeError(
    failure.message ?? 'The legend could not be computed.',
    (failure.errors ?? [])
      .map(mapLegendErrorResourceToAnalyzerError)
      .filter((e) => e.severity === 'Error'),
    err,
  )
}
